import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
  RowSelectionState,
} from "@tanstack/react-table";
import { useState, useRef } from "react";
import { muiTheme } from "@/styles/theme";
import {
  TableContainer,
  Box,
  TableSortLabel,
  SortDirection,
  useTheme,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import CustomCheckbox from "../../ui/checkbox";
import DataTableTopBar from "./top-bar";
import DataTableFooter from "./footer";

type BaseProps<TData, TValue> = {
  name?: string;
  columns: ColumnDef<TData, TValue>[];
  data?: TData[];
  sorting?: SortingState;
  setSorting?: OnChangeFn<SortingState>;
  isLoading?: boolean;
  height: string | number;
  width: string | number;
  columnVisibility?: VisibilityState;
  setColumnVisibility?: OnChangeFn<VisibilityState>;
  showColumnVisibility?: boolean;
  tableLayout?: "fixed" | "full";
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean;
  pageSizeOptions?: number[];
  getRowId?: (row: TData) => string;
  enableColumnResizing?: boolean;
  onRowClick?: (row: TData) => void;
  actions?: React.ReactNode[];
  initialState?: {
    pagination?: PaginationState;
    sorting?: SortingState;
    columnVisibility?: VisibilityState;
  };
  isError?: boolean;
  showTopBar?: boolean;
  defaultColumns?: string[];
  getRowBackgroundColor?: (row: any, defaultColor: string) => string;
  getRowTestId?: (row: any) => string | undefined;
  getRowDataId?: (row: any) => string | undefined;
  extraActions?: React.ReactNode;
  extraRightActions?: React.ReactNode;
};

type ServerPaginationProps<TData, TValue> = BaseProps<TData, TValue> & {
  type: "server";
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  rowCount: number;
  showRowCount?: boolean;
};

type ClientPaginationProps<TData, TValue> = BaseProps<TData, TValue> & {
  type?: "client" | "none";
  pagination?: PaginationState;
  setPagination?: OnChangeFn<PaginationState>;
  rowCount?: number;
  showRowCount?: boolean;
};

export type DataTableProps<TData, TValue = unknown> =
  | ServerPaginationProps<TData, TValue>
  | ClientPaginationProps<TData, TValue>;

export default function DataTable<TData, TValue>({
  name = "Item",
  columnVisibility,
  setColumnVisibility,
  columns,
  data = [],
  type = "server",
  rowCount,
  showRowCount = true,
  pagination,
  setPagination,
  sorting,
  setSorting,
  initialState,
  isLoading,
  height = "100%",
  width = "100%",
  showColumnVisibility = true,
  tableLayout = "fixed",
  rowSelection,
  setRowSelection,
  enableRowSelection = true,
  pageSizeOptions = [10, 20, 50, 100],
  getRowId,
  enableColumnResizing = true,
  onRowClick,
  actions = [],
  isError,
  showTopBar = true,
  defaultColumns = [],
  getRowBackgroundColor,
  getRowTestId,
  getRowDataId,
  extraActions,
  extraRightActions,
}: DataTableProps<TData, TValue>) {
  const theme = useTheme();
  const [internalPagination, setInternalPagination] = useState<PaginationState>(
    initialState?.pagination ?? {
      pageIndex: 0,
      pageSize: pageSizeOptions?.[0] ?? 10, // Default page size for skeleton rows if not overridden
    }
  );

  const [internalSorting, setInternalSorting] = useState<SortingState>(
    initialState?.sorting ?? []
  );

  const [internalVisibility, setInternalColumnVisibility] =
    useState<VisibilityState>(() => {
      // If default columns are provided, initialize with them
      if (defaultColumns.length > 0) {
        const initialVisibility: VisibilityState = {};
        // Set all columns to hidden by default
        columns.forEach((column) => {
          if (column.id) {
            initialVisibility[column.id] = false;
          }
        });
        // Show only default columns
        defaultColumns.forEach((columnId) => {
          initialVisibility[columnId] = true;
        });
        return initialVisibility;
      }
      return columnVisibility ?? {};
    });

  const [internalRowSelection, setInternalRowSelection] =
    useState<RowSelectionState>(rowSelection ?? {});

  const resolvedPagination = pagination ?? internalPagination;
  const resolvedSorting = sorting ?? internalSorting;
  const resolvedColumnVisibility = columnVisibility ?? internalVisibility;
  const resolvedRowSelection = rowSelection ?? internalRowSelection;

  // Track clicked rows for visual feedback
  const [clickedRows, setClickedRows] = useState<Set<string>>(new Set());

  // Pinch-to-zoom state
  const [scale, setScale] = useState(1);
  const [lastDistance, setLastDistance] = useState<number | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handlePaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const newValue =
      typeof updater === "function" ? updater(resolvedPagination) : updater;
    if (setPagination) {
      setPagination(newValue);
    } else {
      setInternalPagination(newValue);
    }
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newValue =
      typeof updater === "function" ? updater(resolvedSorting) : updater; // Use resolvedSorting
    if (setSorting) {
      setSorting(newValue);
    } else {
      setInternalSorting(newValue);
    }
  };

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = (
    updater
  ) => {
    const newValue =
      typeof updater === "function"
        ? updater(resolvedColumnVisibility)
        : updater; // Use resolvedColumnVisibility
    if (setColumnVisibility) {
      setColumnVisibility(newValue);
    } else {
      setInternalColumnVisibility(newValue);
    }
  };

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (updater) => {
    const newValue =
      typeof updater === "function" ? updater(resolvedRowSelection) : updater; // Use resolvedRowSelection
    if (setRowSelection) {
      setRowSelection(newValue);
    } else {
      setInternalRowSelection(newValue);
    }
  };

  // Calculate distance between two touch points
  const getDistance = (touch1: React.Touch, touch2: React.Touch) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start for pinch-to-zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      const distance = getDistance(e.touches[0], e.touches[1]);
      setLastDistance(distance);
    }
  };

  // Handle touch move for pinch-to-zoom
  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastDistance !== null) {
      e.preventDefault(); // Prevent default browser zoom
      const distance = getDistance(e.touches[0], e.touches[1]);
      const ratio = distance / lastDistance;
      
      // Calculate new scale with min/max limits
      const newScale = Math.min(Math.max(scale * ratio, 0.5), 1);
      setScale(newScale);
      setLastDistance(distance);
    }
  };

  // Handle touch end for pinch-to-zoom
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      setLastDistance(null);
    }
  };

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel:
      type !== "none" ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection,
    manualPagination: type === "server",
    manualSorting: type === "server",
    onPaginationChange: type !== "none" ? handlePaginationChange : undefined,
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    rowCount: type === "server" ? rowCount : undefined,
    onRowSelectionChange: handleRowSelectionChange,
    state: {
      pagination: type !== "none" ? resolvedPagination : undefined,
      sorting: resolvedSorting,
      columnVisibility: resolvedColumnVisibility,
      rowSelection: resolvedRowSelection,
    },
    autoResetPageIndex: type !== "server",
    defaultColumn: {
      enableSorting: false,
      size: 200,
      minSize: 100,
      maxSize: 1000,
    },
    columnResizeMode: "onChange",
    getRowId,
    enableColumnResizing,
    initialState: {
      pagination:
        initialState?.pagination ?? (type !== "none" ? resolvedPagination : {}),
      sorting: initialState?.sorting ?? resolvedSorting,
      columnVisibility:
        initialState?.columnVisibility ?? resolvedColumnVisibility,
      rowSelection: resolvedRowSelection,
    },
  });

  const paperBackground = theme.nawy.colors.semantic.layer.secondary || "white";
  const white = theme.nawy.colors.primitives.white[100];
  const dividerColor = theme.nawy.colors.primitives.navy[100];

  const numSkeletonRows =
    type !== "none" ? resolvedPagination.pageSize : pageSizeOptions?.[0] ?? 10;

  return (
    <div
      style={{
        border: `1px solid ${dividerColor}`,
        borderRadius: "8px",
        overflow: "hidden",
        fontFamily: "Regular, sans-serif",
      }}
    >
      {/* Top Bar */}
      {showTopBar && (
        <DataTableTopBar
          name={name}
          type={type}
          rowCount={rowCount}
          showRowCount={showRowCount}
          showColumnVisibility={showColumnVisibility}
          table={table}
          dividerColor={dividerColor}
          paperBackground={white}
          actions={actions}
          defaultColumns={defaultColumns}
          perPage={pagination?.pageSize}
          selectedRowsCount={table.getFilteredSelectedRowModel().rows.length}
          pageIndex={resolvedPagination.pageIndex}
          extraActions={extraActions}
          extraRightActions={extraRightActions}
        />
      )}

      {/* Scrollable Table Area */}
      <TableContainer
        ref={tableContainerRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          height: `calc(${
            typeof height === "string" ? height : `${height}px`
          } - 56px ${type !== "none" ? "- 51px" : ""})`,
          width,
          maxWidth: width,
          overflowY: "auto",
          overflowX: "auto",
          backgroundColor: "transparent",
          fontFamily: "Regular, sans-serif",
          touchAction: "pan-x pan-y", // Allow scrolling but we'll handle pinch zoom
          // Custom scrollbar styling (only when not auto height)
          ...(height !== "auto" && {
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#F3F4F6",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#D1D5DB",
              borderRadius: "4px",
              "&:hover": { backgroundColor: "#9CA3AF" },
            },
            "&::-webkit-scrollbar-thumb:active": { backgroundColor: "#6B7280" },
            scrollbarWidth: "thin",
            scrollbarColor: "#D1D5DB #F3F4F6",
          }),
        }}
      >
        <div
          style={{
            width: `${100 * scale}%`,
            height: `${100 * scale}%`,
            transition: "width 0.1s ease-out, height 0.1s ease-out",
          }}
        >
          <table
            style={{
              width:
                tableLayout === "fixed" ? table.getCenterTotalSize() : "100%",
              fontFamily: "Regular, sans-serif",
              transform: `scale(${scale})`,
              transformOrigin: "top left",
              transition: "transform 0.1s ease-out",
            }}
          >
          {/* Sticky Header container */}
          <thead
            style={{
              position: "sticky",
              top: 0,
              zIndex: 100, // Increased z-index to be well above table content
              backgroundColor: paperBackground,
              boxShadow: `inset 0 0.3px 0 0 ${dividerColor}, inset 0 -0.3px 0 0 ${dividerColor}, inset 0.3px 0 0 0 ${dividerColor}`,
            }}
          >
            {table.getHeaderGroups().map((headerGroup) => {
              const isLeaf = headerGroup.depth === table.getExpandedDepth();

              return (
                <tr
                  key={headerGroup.id}
                  style={{
                    height: "44px",
                    borderBottom: `1px solid ${dividerColor}`,
                  }}
                >
                  {isLeaf && enableRowSelection ? (
                    <th
                      style={{
                        width: "44px", // Width of the select all checkbox column
                        position: "relative",
                        zIndex: 10, // Ensure header is above table rows
                        borderRight: `1px solid ${dividerColor}`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CustomCheckbox
                        indeterminate={table.getIsSomeRowsSelected()}
                        checked={table.getIsAllRowsSelected()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.stopPropagation();
                          table.getToggleAllRowsSelectedHandler()(e);
                        }}
                        inputProps={{
                          "aria-label": "select all",
                        }}
                      />
                    </th>
                  ) : null}
                  {headerGroup.headers.map((header, headerIndex) => {
                    let sortDirection: SortDirection | undefined =
                      header.column.getIsSorted();

                    if (sortDirection === false) {
                      sortDirection = undefined;
                    }

                    const sortDisabled =
                      !header.column.getCanSort() || header.isPlaceholder;

                    const isLastHeader =
                      headerIndex === headerGroup.headers.length - 1;

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{
                          padding: "12px",
                          maxWidth: header.getSize(),
                          width: header.getSize(),
                          color: sortDirection
                            ? theme.nawy.colors.semantic.text.primary
                            : theme.nawy.colors.semantic.text.secondary,
                          borderRight: !isLastHeader
                            ? `1px solid ${dividerColor}`
                            : undefined,
                          fontFamily: "SemiBold, sans-serif",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            position: "relative",
                          }}
                        >
                          <TableSortLabel
                            hideSortIcon={sortDisabled}
                            active={
                              sortDirection !== undefined && !sortDisabled
                            }
                            direction={sortDirection}
                            onClick={header.column.getToggleSortingHandler()}
                            sx={{
                              ...theme.nawy.typography.english.variants.body.sm
                                .semibold,
                              color: theme.nawy.colors.semantic.text.primary,
                              cursor: !sortDisabled ? "pointer" : "default",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            {header.column.getIsSorted() ? (
                              <Box component="span" sx={visuallyHidden}>
                                {header.column.getIsSorted() === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>

                          <div style={{ flexGrow: 1 }} />

                          {header.column.getCanResize() && (
                            <Box
                              data-state={
                                header.column.getIsResizing() && "resizing"
                              }
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              sx={{
                                position: "absolute",
                                right: "-15px",
                                top: "-12px",
                                height: "calc(100% + 24px)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "6px",
                                cursor: "col-resize",
                                transform: header.column.getIsResizing()
                                  ? `translateX(${
                                      table.getState().columnSizingInfo
                                        .deltaOffset
                                    })`
                                  : "",
                                transition: "transform 0.2s ease",
                                "&:hover": {
                                  backgroundColor:
                                    theme.nawy.colors.primitives.grey[300],
                                },
                                "&[data-state='resizing']": {
                                  backgroundColor:
                                    theme.nawy.colors.semantic.layer.accent,
                                },
                              }}
                            />
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          {/* Table Body */}
          <tbody>
            {!isError &&
              isLoading &&
              Array.from({ length: numSkeletonRows }).map((_, index) => (
                <tr // Skeleton Row
                  key={`skeleton-${index}`}
                  style={{
                    borderBottom: `1px solid ${dividerColor}`,
                  }}
                >
                  {enableRowSelection ? (
                    <td
                      style={{
                        minWidth: "44px",
                        padding: "10px",
                        borderRight: `1px solid ${dividerColor}`,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                        }}
                      >
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            backgroundColor: "grey.200",
                            borderRadius: "3px",
                          }}
                        />
                      </div>
                    </td>
                  ) : null}
                  {table.getVisibleLeafColumns().map((column, colIndex) => {
                    const isLastCol =
                      colIndex === table.getVisibleLeafColumns().length - 1;
                    return (
                      <td
                        key={column.id}
                        style={{
                          width: column.getSize(),
                          maxWidth: column.getSize(),
                          padding: "12px",
                          borderRight: !isLastCol
                            ? `1px solid ${dividerColor}`
                            : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            height: 20,
                            backgroundColor: "grey.200",
                            borderRadius: "4px",
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            {!isError &&
              !isLoading &&
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  data-testid={getRowTestId?.(row)}
                  data-row-id={getRowDataId?.(row)}
                  style={{
                    cursor: onRowClick ? "pointer" : "default",
                    borderBottom: `1px solid ${dividerColor}`,
                    backgroundColor: row.getIsSelected()
                      ? theme.nawy.colors.primitives.grey[100]
                      : clickedRows.has(row.id)
                      ? "#b3d9e6" // Darker blue for clicked state
                      : getRowBackgroundColor?.(row, "transparent") ||
                        "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!row.getIsSelected() && !clickedRows.has(row.id)) {
                      e.currentTarget.style.backgroundColor =
                        theme.nawy.colors.primitives.grey[50]!;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!row.getIsSelected() && !clickedRows.has(row.id)) {
                      e.currentTarget.style.backgroundColor =
                        getRowBackgroundColor?.(row, "transparent") ||
                        "transparent";
                    }
                  }}
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(row.original);
                      // Add click visual feedback
                      setClickedRows((prev) => {
                        const newSet = new Set(prev);
                        newSet.add(row.id);
                        return newSet;
                      });
                      // Remove click state after a short delay
                      setTimeout(() => {
                        setClickedRows((prev) => {
                          const newSet = new Set(prev);
                          newSet.delete(row.id);
                          return newSet;
                        });
                      }, 200);
                    }
                  }}
                >
                  {row.getCanSelect() ? (
                    <td
                      style={{
                        width: "44px",
                        borderRight: `1px solid ${dividerColor}`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <CustomCheckbox
                        checked={row.getIsSelected()}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          e.stopPropagation();
                          row.getToggleSelectedHandler()(e);
                        }}
                        inputProps={{
                          "aria-labelledby": `select-${row.id}`,
                        }}
                      />
                    </td>
                  ) : null}
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const isLastCell =
                      cellIndex === row.getVisibleCells().length - 1;
                    return (
                      <td
                        onClick={(e) => {
                          // Check if the click is on an edit button or modal
                          const target = e.target as HTMLElement;
                          if (
                            target.closest(".edit-status-icon") ||
                            target.closest(".edit-agent-icon") ||
                            target.closest(".lucide-copy") ||
                            target.closest('[role="dialog"]') ||
                            target.closest('[data-modal="true"]')
                          ) {
                            return;
                          }
                          onRowClick?.(row.original);
                          e.stopPropagation(); // Stop event from bubbling up
                        }}
                        key={cell.id}
                        style={{
                          maxWidth: cell.column.getSize(),
                          width: cell.column.getSize(),
                          padding: "12px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: "Regular, sans-serif",
                          ...theme.nawy.typography.english.variants.body.md
                            .regular,
                          color: theme.nawy.colors.primitives.grey[900],
                          borderRight: !isLastCell
                            ? `1px solid ${dividerColor}`
                            : undefined,
                          minWidth: "70px",
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            {!isError &&
              !isLoading &&
              table.getRowModel().rows.length === 0 && (
                <tr
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    width: "100%",
                    color: theme.nawy.colors.semantic.text.secondary,
                  }}
                >
                  <td
                    colSpan={table.getVisibleLeafColumns().length + 1}
                    rowSpan={table.getRowModel().rows.length}
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      width: "100%",
                      color: theme.nawy.colors.semantic.text.secondary,
                      ...theme.nawy.typography.english.variants.body.md.regular,
                    }}
                  >
                    No data available.
                  </td>
                </tr>
              )}
            {isError && (
              <tr
                style={{
                  padding: "20px",
                  textAlign: "center",
                  width: "100%",
                  color: muiTheme.colorSchemes.light.palette.error.main,
                }}
              >
                <td
                  colSpan={table.getVisibleLeafColumns().length + 1}
                  rowSpan={table.getRowModel().rows.length}
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    width: "100%",
                    color: theme.nawy.colors.semantic.text.secondary,
                    ...theme.nawy.typography.english.variants.body.md.regular,
                  }}
                >
                  An error occurred while fetching data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </TableContainer>
      {type !== "none" && (
        <DataTableFooter
          type={type}
          table={table}
          rowsCount={rowCount}
          dividerColor={dividerColor}
          paperBackground={white}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </div>
  );
}
